import './home-widgets.css';
import { useId, useRef, useState } from 'react';
import { ArrowRight, CircleAlert, LockKeyhole } from 'lucide-react';
import {
  Badge,
  Button,
  EntityText,
  Input,
  Skeleton,
  Stat,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Timeline,
  TimeGrid,
  Tooltip,
  cn,
} from '@/components/ui';
import { BarChart } from '@/components/charts/BarChart';
import { LineChart } from '@/components/charts/LineChart';
import { DailyHeatmap } from '@/components/charts/DailyHeatmap';
import { buildDailyHeatmap } from '@/components/charts/chartData';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { HomeWidgetFrame, type HomeWidgetActions } from './HomeWidgetFrame';
import { HomeChartDataDialog } from './HomeChartDataDialog';
import {
  homeWidgetDefinitionError,
  homeWidgetSettingsError,
  homeWidgetSizeError,
  homeWidgetStateError,
  homeWidgetVisibleLimit,
  type HomeWidgetData,
  type HomeWidgetDefinition,
  type HomeWidgetInstance,
  type HomeWidgetKind,
  type HomeWidgetState,
} from './homeWidgetData';
export interface HomeWidgetProps extends HomeWidgetActions {
  definition: HomeWidgetDefinition;
  instance: HomeWidgetInstance;
  state: HomeWidgetState;
  now?: number;
  onObjectOpen?: (id: string) => void;
  onAction?: (input: string) => void | Promise<void>;
}
const exactChartNumber = (value: number) => String(value).replace('.', ',').replace(/^-/, '−');
function chartTable(data: Extract<HomeWidgetData, { type: 'chart' }>) {
  if (data.variant === 'bar')
    return {
      headers: ['Категория', ...data.series.map((series) => series.label)],
      rows: data.categories.map((category, index) => [
        category.longLabel ?? category.label,
        ...data.series.map((series) => {
          const value = series.values[index];
          return value == null ? 'Нет данных' : exactChartNumber(value);
        }),
      ]),
    };
  if (data.variant === 'line') {
    const pointsByTime = new Map<number, Map<string, number>>();
    for (const series of data.series)
      for (const point of series.points) {
        const row = pointsByTime.get(point.x) ?? new Map<string, number>();
        row.set(series.id, point.y);
        pointsByTime.set(point.x, row);
      }
    return {
      headers: ['Время (UTC)', ...data.series.map((series) => series.label)],
      rows: [...pointsByTime.entries()]
        .sort(([left], [right]) => left - right)
        .map(([timestamp, values]) => [
          Number.isNaN(new Date(timestamp).getTime())
            ? String(timestamp) + ' мс (вне диапазона даты)'
            : new Date(timestamp).toISOString(),
          ...data.series.map((series) => {
            const value = values.get(series.id);
            return value === undefined
              ? 'Нет данных'
              : exactChartNumber(value) + (data.unit ? ' ' + data.unit : '');
          }),
        ]),
    };
  }
  const model = buildDailyHeatmap(data.from, data.to, data.values, data.domainMax);
  return {
    headers: ['Дата', 'Значение'],
    rows: model.error
      ? []
      : model.weeks
          .flat()
          .flatMap((cell) =>
            cell
              ? [[cell.date, cell.value === null ? 'Нет данных' : exactChartNumber(cell.value) + ' шт.']]
              : [],
          ),
  };
}
function WidgetSkeleton({
  kind,
  chartVariant,
}: {
  kind: HomeWidgetKind;
  chartVariant?: HomeWidgetDefinition['chartVariant'];
}) {
  return (
    <div data-home-widget-skeleton={kind} aria-label="Загрузка виджета" className="space-y-3">
      {kind === 'stat' ? (
        <>
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-3 w-32" />
        </>
      ) : kind === 'chart' ? (
        chartVariant === 'heatmap' ? (
          <div data-home-chart-skeleton="heatmap" className="grid grid-cols-7 gap-1">
            {Array.from({ length: 49 }, (_, index) => (
              <Skeleton key={index} className="aspect-square w-full rounded-xs" />
            ))}
          </div>
        ) : chartVariant === 'line' ? (
          <svg data-home-chart-skeleton="line" aria-hidden viewBox="0 0 272 128" className="h-40 w-full">
            <path
              d="M8 104C28 104 30 80 52 80S76 64 98 64S130 36 160 36S186 56 212 44S236 24 264 24"
              fill="none"
              stroke="var(--muted)"
              strokeWidth="8"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <div data-home-chart-skeleton="bar" className="flex h-40 items-end gap-3">
            {['h-24', 'h-32', 'h-20', 'h-28'].map((height, index) => (
              <Skeleton key={index} className={cn('flex-1', height)} />
            ))}
          </div>
        )
      ) : kind === 'mini-table' ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }, (_, row) => (
            <div key={row} className="grid grid-cols-3 gap-3">
              {Array.from({ length: 3 }, (_, column) => (
                <Skeleton key={column} className="h-4 w-full" />
              ))}
            </div>
          ))}
        </div>
      ) : kind === 'cells' ? (
        <div className="grid grid-cols-6 gap-2">
          {Array.from({ length: 18 }, (_, index) => (
            <Skeleton key={index} className="aspect-square w-full rounded-xs" />
          ))}
        </div>
      ) : kind === 'week-grid' ? (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 21 }, (_, index) => (
            <Skeleton key={index} className="h-8 w-full rounded-xs" />
          ))}
        </div>
      ) : kind === 'quick-action' ? (
        <div className="flex gap-2">
          <Skeleton className="h-9 flex-1" />
          <Skeleton className="h-9 w-20" />
        </div>
      ) : kind === 'note' ? (
        <>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
          <Skeleton className="h-3 w-2/3" />
        </>
      ) : (
        [0, 1, 2].map((index) => (
          <div key={index} className="flex items-center gap-3">
            {kind === 'event-feed' && <Skeleton className="size-2 rounded-full" />}
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
            {kind !== 'event-feed' && <Skeleton className="h-5 w-12 rounded-full" />}
          </div>
        ))
      )}
    </div>
  );
}
function More({ count, onOpen }: { count: number; onOpen?: () => void }) {
  return count > 0 ? (
    onOpen ? (
      <Button type="button" variant="link" size="sm" className="mt-2" onClick={onOpen}>
        Ещё {count}
        <ArrowRight className="size-3.5" />
      </Button>
    ) : (
      <p className="mt-2 text-xs text-fg-muted">Ещё {count} →</p>
    )
  ) : null;
}
function QuickAction({
  data,
  onAction,
}: {
  data: Extract<HomeWidgetData, { type: 'quick-action' }>;
  onAction?: HomeWidgetProps['onAction'];
}) {
  const id = useId(),
    [input, setInput] = useState(''),
    [pending, setPending] = useState(false),
    [error, setError] = useState(''),
    [done, setDone] = useState(false);
  async function submit() {
    if (!onAction || pending) return;
    if (data.inputLabel && !input.trim()) {
      setError('Заполни поле.');
      return;
    }
    setPending(true);
    setError('');
    setDone(false);
    try {
      await onAction(input);
      setInput('');
      setDone(true);
    } catch {
      setError('Не получилось. Ввод сохранён; повтори.');
    } finally {
      setPending(false);
    }
  }
  return (
    <div className="space-y-2">
      <form
        aria-label={data.label}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="flex min-w-0 items-start gap-2"
      >
        {data.inputLabel && (
          <div className="min-w-0 flex-1">
            <label className="sr-only" htmlFor={id}>
              {data.inputLabel}
            </label>
            <Input
              id={id}
              value={input}
              onChange={(event) => {
                setInput(event.target.value);
                setDone(false);
              }}
              placeholder={data.placeholder}
              disabled={pending || !onAction}
              aria-invalid={!!error}
            />
          </div>
        )}
        <Button type="submit" variant="secondary" size="md" disabled={pending || !onAction}>
          {pending ? 'Выполнение…' : data.buttonLabel}
        </Button>
      </form>
      {error && (
        <p role="alert" className="text-xs text-danger-strong">
          {error}
        </p>
      )}
      {done && (
        <p role="status" className="text-xs text-success-strong">
          Готово
        </p>
      )}
    </div>
  );
}
const cellTone = {
  ok: 'border-success-line bg-success-soft text-success-strong',
  warning: 'border-warning-line bg-warning-soft text-warning-strong',
  error: 'border-danger-line bg-danger-soft text-danger-strong',
  unknown: 'border-line bg-muted text-fg-subtle',
};
const cellLabel = { ok: 'Готово', warning: 'Внимание', error: 'Ошибка', unknown: 'Нет данных' };
function WidgetContent({
  definition,
  instance,
  data,
  onObjectOpen,
  onAction,
  onOpenApplication,
  now,
}: HomeWidgetProps & { data: HomeWidgetData }) {
  const limit = homeWidgetVisibleLimit(data.type, instance.size),
    empty = <p className="text-sm text-fg-muted">Пока нет данных</p>;
  switch (data.type) {
    case 'stat':
      return (
        <Stat
          label={data.label}
          value={data.value}
          unit={data.unit}
          delta={data.delta}
          trend={data.trend ? [...data.trend] : undefined}
          className="gap-1 border-0 bg-transparent p-0"
        />
      );
    case 'status-list': {
      const shown = data.items.slice(0, limit);
      return shown.length ? (
        <>
          <ul className="divide-y divide-line">
            {shown.map((item) => (
              <li key={item.id} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  {onObjectOpen ? (
                    <EntityText name={item.title} onClick={() => onObjectOpen(item.id)} />
                  ) : (
                    <span className="block truncate text-sm font-medium" title={item.title}>
                      {item.title}
                    </span>
                  )}
                  {item.detail && <p className="mt-1 truncate text-xs text-fg-muted">{item.detail}</p>}
                </div>
                <Badge variant={item.status.tone} solid className="shrink-0">
                  {item.status.label}
                </Badge>
              </li>
            ))}
          </ul>
          <More count={(data.total ?? data.items.length) - shown.length} onOpen={onOpenApplication} />
        </>
      ) : (
        empty
      );
    }
    case 'chart':
      if (data.variant === 'bar')
        return (
          <BarChart
            label={definition.title}
            showKeyboardHint={false}
            categories={data.categories}
            series={data.series}
          />
        );
      if (data.variant === 'line')
        return (
          <LineChart
            ariaLabel={definition.title}
            height={instance.size.rows === 2 ? 192 : 256}
            unit={data.unit}
            series={data.series.map((series) => ({
              ...series,
              points: [...series.points],
              color: 'var(--chart-' + (series.color ?? 2) + ')',
            }))}
          />
        );
      return (
        <div>
          <DailyHeatmap
            label={definition.title}
            from={data.from}
            to={data.to}
            values={data.values}
            domainMax={data.domainMax}
          />
        </div>
      );
    case 'mini-table': {
      const shown = data.rows.slice(0, limit);
      return shown.length ? (
        <>
          <Table dense>
            <TableHeader>
              <TableRow>
                {data.columns.map((column) => (
                  <TableHead
                    key={column.id}
                    className={column.numeric ? 'text-right whitespace-nowrap' : 'whitespace-nowrap'}
                  >
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((row) => (
                <TableRow key={row.id}>
                  {data.columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={column.numeric ? 'text-right tabular-nums' : 'text-xs'}
                    >
                      {row.cells[column.id] ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <More count={(data.total ?? data.rows.length) - shown.length} onOpen={onOpenApplication} />
        </>
      ) : (
        empty
      );
    }
    case 'event-feed': {
      const shown = data.items.slice(0, limit);
      return shown.length ? (
        <>
          <Timeline
            items={shown.map((item) => ({
              ...item,
              time: new Intl.DateTimeFormat('ru-RU', {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Europe/Moscow',
              }).format(new Date(item.at)),
              dateTime: item.at,
            }))}
          />
          <More count={(data.total ?? data.items.length) - shown.length} onOpen={onOpenApplication} />
        </>
      ) : (
        empty
      );
    }
    case 'cells': {
      const shown = data.items.slice(0, limit);
      return shown.length ? (
        <>
          <div className="grid grid-cols-6 gap-2">
            {shown.map((item, index) => (
              <Tooltip key={item.id} content={item.label + ' · ' + cellLabel[item.status]}>
                <button
                  type="button"
                  aria-label={item.label + ': ' + cellLabel[item.status]}
                  onClick={() => onObjectOpen?.(item.id)}
                  className={cn(
                    'grid aspect-square min-w-0 place-items-center rounded-xs border text-2xs font-medium tabular-nums focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                    cellTone[item.status],
                  )}
                >
                  {index + 1}
                </button>
              </Tooltip>
            ))}
          </div>
          <More count={(data.total ?? data.items.length) - shown.length} onOpen={onOpenApplication} />
        </>
      ) : (
        empty
      );
    }
    case 'week-grid': {
      const rows = data.rows.slice(0, 3),
        ids = new Set(rows.map((row) => row.id));
      return (
        <div>
          <TimeGrid
            label={definition.title}
            rows={rows}
            shifts={data.shifts.filter((shift) => ids.has(shift.rowId))}
            scale="week"
            anchor={data.anchor}
            periods={1}
            timeZone={data.timeZone}
            now={now}
            rowHeading="Участник"
            highlightWeekends
            onShiftClick={(shift) => onObjectOpen?.(shift.id)}
          />
          {data.rows.length > rows.length && (
            <More count={data.rows.length - rows.length} onOpen={onOpenApplication} />
          )}
        </div>
      );
    }
    case 'quick-action':
      return <QuickAction data={data} onAction={onAction} />;
    case 'note':
      return (
        <div className="space-y-3">
          {data.text && <p className="text-sm break-words whitespace-pre-wrap">{data.text}</p>}
          {data.links?.length ? (
            <ul className="space-y-2">
              {data.links.slice(0, limit).map((link) => (
                <li key={link.id}>
                  <a
                    href={link.href}
                    target={link.href.startsWith('/') ? undefined : '_blank'}
                    rel={link.href.startsWith('/') ? undefined : 'noopener noreferrer'}
                    className="rounded-xs text-sm text-brand-fg underline decoration-dotted underline-offset-4 hover:decoration-solid focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
          {!data.text && !data.links?.length && empty}
          {data.links && <More count={data.links.length - limit} onOpen={onOpenApplication} />}
        </div>
      );
  }
}
export function HomeWidget(props: HomeWidgetProps) {
  const { definition, instance, state, onRetry } = props;
  const [tableOpen, setTableOpen] = useState(false),
    returnFocusRef = useRef<HTMLElement | null>(null);
  const invalid =
    homeWidgetDefinitionError(definition) ??
    homeWidgetSizeError(definition.type, instance.size) ??
    homeWidgetSettingsError(instance.settings) ??
    homeWidgetStateError(state, definition.type);
  if (
    invalid ||
    instance.definitionId !== definition.id ||
    (state.state === 'ready' &&
      state.data.type === 'chart' &&
      definition.chartVariant !== undefined &&
      state.data.variant !== definition.chartVariant) ||
    !definition.allowedSizes.some(
      (size) => size.columns === instance.size.columns && size.rows === instance.size.rows,
    )
  )
    return (
      <div
        data-slot="home-widget-invalid"
        role="alert"
        className="rounded-lg border border-line bg-card p-4 text-sm text-fg-muted"
      >
        Виджет недоступен
      </div>
    );
  const failed = (message: string) => (
    <div className="space-y-3">
      <p className="inline-flex items-center gap-2 text-sm text-fg-muted">
        <CircleAlert className="size-4" />
        {message}
      </p>
      {onRetry && (
        <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
          Повторить
        </Button>
      )}
    </div>
  );
  return (
    <HomeWidgetFrame
      {...props}
      onViewTable={
        state.state === 'ready' && state.data.type === 'chart'
          ? () => {
              returnFocusRef.current =
                document.activeElement instanceof HTMLElement ? document.activeElement : null;
              setTableOpen(true);
            }
          : undefined
      }
    >
      <>
        <ErrorBoundary
          resetKey={instance.instanceId + ':' + state.state}
          fallback={(_error, reset) => (
            <div className="space-y-3">
              <p className="text-sm text-fg-muted">Ошибка виджета</p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  reset();
                  onRetry?.();
                }}
              >
                Повторить
              </Button>
            </div>
          )}
        >
          {state.state === 'loading' ? (
            <WidgetSkeleton kind={definition.type} chartVariant={definition.chartVariant} />
          ) : state.state === 'no-access' ? (
            <p className="inline-flex items-center gap-2 text-sm text-fg-muted">
              <LockKeyhole className="size-4" />
              Нет доступа
            </p>
          ) : state.state === 'error' ? (
            failed(state.message ?? 'Ошибка виджета')
          ) : (
            <WidgetContent {...props} data={state.data} />
          )}
        </ErrorBoundary>
        {state.state === 'ready' && state.data.type === 'chart' && (
          <HomeChartDataDialog
            open={tableOpen}
            title={definition.title}
            {...chartTable(state.data)}
            returnFocusRef={returnFocusRef}
            onOpenChange={setTableOpen}
          />
        )}
      </>
    </HomeWidgetFrame>
  );
}
